import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { Wallet, LogOut, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const WalletButton = ({ className = "", testid = "wallet-connect-button" }) => {
  const { wallet, wallets, select, connect, disconnect, connecting, connected, publicKey } =
    useWallet();
  const [open, setOpen] = useState(false);
  const [pendingName, setPendingName] = useState(null);

  // After select() updates the wallet ref, fire connect()
  useEffect(() => {
    if (pendingName && wallet?.adapter?.name === pendingName) {
      connect()
        .catch((e) => toast.error(e?.message || "Connect failed"))
        .finally(() => setPendingName(null));
      setOpen(false);
    }
  }, [pendingName, wallet, connect]);

  const handleClick = async () => {
    try {
      if (connected) {
        await disconnect();
        return;
      }
      setOpen(true);
    } catch (e) {
      toast.error(e?.message || "Wallet error");
    }
  };

  const chooseWallet = (name) => {
    const w = wallets.find((x) => x.adapter.name === name);
    if (!w) return;
    // If installed/detected → connect via select() effect
    if (w.readyState === "Installed" || w.readyState === "Loadable" || name === "WalletConnect") {
      select(name);
      setPendingName(name);
    } else {
      // Not installed → open install page
      const urls = {
        Phantom: "https://phantom.com/download",
        Solflare: "https://solflare.com/download",
        "Trust": "https://trustwallet.com/download",
      };
      const url = urls[name];
      if (url) window.open(url, "_blank");
      else toast.error(`${name} not detected`);
    }
  };

  const short = publicKey
    ? `${publicKey.toBase58().slice(0, 4)}…${publicKey.toBase58().slice(-4)}`
    : null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={testid}
        className={`nb-btn nb-btn--white ${className}`}
        style={{ fontSize: "1rem", padding: "0.7rem 1.1rem" }}
      >
        {connected ? <LogOut size={18} /> : <Wallet size={18} />}
        {connecting ? "Connecting…" : connected ? short : "Connect Wallet"}
      </button>

      {open && (
        <WalletPickerModal
          wallets={wallets}
          onPick={chooseWallet}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const WalletPickerModal = ({ wallets, onPick, onClose }) => {
  // Display the curated set in this order, regardless of detection
  const order = ["Phantom", "Solflare", "Trust", "WalletConnect"];
  const map = new Map(wallets.map((w) => [w.adapter.name, w]));
  const list = order
    .map((name) => ({ name, w: map.get(name) }))
    .filter((x) => x.w);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0, 0, 0, 0.72)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="wallet-picker-modal"
    >
      <div
        className="gg-panel gg-glow-cyan rounded-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded-lg"
          data-testid="wallet-picker-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <h3 className="font-display text-2xl uppercase mb-1">Connect a Wallet</h3>
        <p className="text-sm text-[var(--jd-ocean)] mb-5">
          Choose a Solana wallet to ride the JetDog
        </p>

        <div className="space-y-2.5">
          {list.map(({ name, w }) => {
            const detected =
              w.readyState === "Installed" || w.readyState === "Loadable";
            const isWC = name === "WalletConnect";
            return (
              <button
                type="button"
                key={name}
                onClick={() => onPick(name)}
                data-testid={`wallet-option-${name.toLowerCase()}`}
                className="w-full flex items-center gap-3 p-3 rounded-2xl nb-border hover:border-[rgba(0,229,255,0.6)] hover:bg-white/5 transition-colors"
              >
                <WalletIcon name={name} icon={w.adapter.icon} />
                <div className="flex-1 text-left">
                  <p className="font-display text-base uppercase leading-tight">
                    {name === "Trust" ? "Trust Wallet" : name}
                  </p>
                  <p className="text-xs text-[var(--jd-ocean)]">
                    {isWC
                      ? "Scan with any mobile wallet"
                      : detected
                      ? "Detected"
                      : "Not installed — click to get it"}
                  </p>
                </div>
                {!detected && !isWC && <ExternalLink size={14} />}
              </button>
            );
          })}
        </div>

        <p className="text-[11px] text-[var(--jd-ocean)] mt-5">
          $JETDOG is a Solana SPL Token-2022. Only Solana-compatible wallets are supported.
        </p>
      </div>
    </div>,
    document.body
  );
};

const WalletIcon = ({ name, icon }) => {
  if (icon) {
    return (
      <img
        src={icon}
        alt={`${name} icon`}
        className="w-8 h-8 rounded-lg"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-lg bg-[var(--jd-primary)] flex items-center justify-center">
      <Wallet size={16} />
    </div>
  );
};

export default WalletButton;
